/**
 * Mis pagos (`/pagos`).
 *
 * Portado de `PortalCliente::pagos()` + `api_pagos_balance` + `api_pagos_movimientos`:
 * el estado de cuenta del cliente en un rango de fechas (saldo inicial, cargos,
 * retención, abonado y saldo actual), las facturas del período con su estado de
 * pago y el historial de abonos.
 *
 * El portal antiguo pedía el `credito_id` y validaba que fuera del cliente; aquí
 * el filtro por el cliente del token ya lo garantiza, así que el rango de fechas
 * es lo único que se elige.
 */

import { CreditCard, Download, Receipt, RotateCcw, Search, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { exportarCsv } from '@/lib/export';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue } from '@/lib/utils';

import { usePortalBalance, usePortalMovimientos } from '../portal.hooks';

const TONO_ESTADO: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  Pagado: 'success',
  Vigente: 'neutral',
  'Por vencer': 'warning',
  Vencido: 'danger',
};

function inicioDeMes(): string {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

export function PortalPagosPage() {
  useDocumentTitle('Portal · Pagos');
  const [parametros, setParametros] = useSearchParams();

  const filtros = useMemo(
    () => ({
      desde: parametros.get('desde') ?? inicioDeMes(),
      hasta: parametros.get('hasta') ?? new Date().toISOString().slice(0, 10),
    }),
    [parametros],
  );

  const balance = usePortalBalance(filtros);
  const movimientos = usePortalMovimientos(filtros);

  const [busquedaFacturas, setBusquedaFacturas] = useState('');
  const [busquedaAbonos, setBusquedaAbonos] = useState('');

  const facturasPeriodo = useMemo(() => balance.data?.facturas ?? [], [balance.data]);
  const movimientosPeriodo = useMemo(
    () => movimientos.data?.movimientos ?? [],
    [movimientos.data],
  );

  const facturasFiltradas = useMemo(
    () =>
      facturasPeriodo.filter((factura) =>
        coincideBusqueda(
          [
            factura.documento,
            factura.estado,
            formatDateValue(factura.emitida),
            formatDateValue(factura.vencimiento),
            formatCurrency(factura.total),
            formatCurrency(factura.pendiente),
          ],
          busquedaFacturas,
        ),
      ),
    [facturasPeriodo, busquedaFacturas],
  );

  const abonosFiltrados = useMemo(
    () =>
      movimientosPeriodo.filter((movimiento) =>
        coincideBusqueda(
          [
            movimiento.referencia,
            movimiento.metodo,
            movimiento.cuenta,
            movimiento.detalle,
            formatDateValue(movimiento.fecha),
            formatCurrency(movimiento.monto),
          ],
          busquedaAbonos,
        ),
      ),
    [movimientosPeriodo, busquedaAbonos],
  );

  const paginacionFacturas = usePaginacion(facturasFiltradas, {
    clave: `${busquedaFacturas}|${filtros.desde}|${filtros.hasta}`,
  });

  const paginacionAbonos = usePaginacion(abonosFiltrados, {
    clave: `${busquedaAbonos}|${filtros.desde}|${filtros.hasta}`,
  });

  const cambiar = (campo: 'desde' | 'hasta', valor: string) => {
    const siguiente = new URLSearchParams(parametros);
    siguiente.set(campo, valor);
    setParametros(siguiente, { replace: true });
  };

  const exportar = () => {
    const pagos = abonosFiltrados.map((movimiento) => [
      formatDateValue(movimiento.fecha),
      movimiento.referencia ?? '',
      movimiento.metodo,
      movimiento.cuenta ?? '',
      movimiento.monto.toFixed(2),
      movimiento.detalle ?? '',
    ]);

    exportarCsv(
      'pagos-portal',
      ['Fecha', 'Referencia', 'Método', 'Cuenta', 'Monto', 'Detalle'],
      pagos,
    );
  };

  const datos = balance.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={CreditCard}
          titulo="Mis pagos"
          descripcion="Estado de cuenta del período: cargos, retenciones y abonos."
        />
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          disabled={abonosFiltrados.length === 0}
          onClick={exportar}
        >
          Descargar CSV
        </Button>
      </div>

      <SectionCard title="Período" description="Por defecto, el mes en curso.">
        <div className="grid gap-3 sm:grid-cols-2 xl:max-w-xl">
          <Input
            label="Desde"
            type="date"
            value={filtros.desde}
            onChange={(evento) => cambiar('desde', evento.target.value)}
          />
          <Input
            label="Hasta"
            type="date"
            value={filtros.hasta}
            onChange={(evento) => cambiar('hasta', evento.target.value)}
          />
        </div>
      </SectionCard>

      {balance.error ? (
        <Alert variant="error">
          {balance.error instanceof ApiError
            ? balance.error.message
            : 'No se pudo calcular tu saldo.'}
        </Alert>
      ) : null}

      <SectionCard title="Estado de cuenta" description="Los mismos números que usa la estación para tu cobranza.">
        {balance.isLoading || !datos ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {[
              { etiqueta: 'Saldo anterior', valor: datos.saldo_inicial, tono: 'text-ink-900' },
              { etiqueta: 'Cargos del período', valor: datos.total_cargos, tono: 'text-ink-900' },
              { etiqueta: 'Retenciones', valor: datos.total_retencion, tono: 'text-ink-600' },
              { etiqueta: 'Abonado', valor: datos.total_abonado, tono: 'text-emerald-600' },
              { etiqueta: 'Saldo actual', valor: datos.saldo_actual, tono: 'text-amber-600' },
            ].map((dato) => (
              <div key={dato.etiqueta} className="rounded-control border border-ink-100 px-3 py-2">
                <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                  {dato.etiqueta}
                </p>
                <p className={`truncate text-base font-semibold tabular-nums ${dato.tono}`} title={formatCurrency(dato.valor)}>
                  {formatCurrency(dato.valor)}
                </p>
              </div>
            ))}
          </div>
        )}

        {datos && datos.vencido > 0 ? (
          <Alert variant="warning" title="Tienes documentos vencidos">
            {formatCurrency(datos.vencido)} vencidos dentro del período mostrado.
          </Alert>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Facturas del período"
        description={
          balance.data
            ? `${busquedaFacturas.trim() ? `${facturasFiltradas.length} de ${facturasPeriodo.length}` : `${facturasPeriodo.length}`} documento(s)`
            : 'Cargando…'
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busquedaFacturas}
            onChange={setBusquedaFacturas}
            ariaLabel="Buscar facturas del período"
            placeholder="Buscar documento o estado…"
          />

          {balance.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : facturasPeriodo.length === 0 ? (
            <EstadoVacio
              icon={Receipt}
              titulo="Sin facturas en esas fechas"
              detalle="No hay facturas a crédito emitidas en el rango elegido."
            />
          ) : facturasFiltradas.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos facturas para “${busquedaFacturas.trim()}”.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusquedaFacturas('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Documento</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Emitida</th>
                  <th className="py-2 font-semibold">Vence</th>
                  <th className="hidden py-2 text-right font-semibold sm:table-cell">Total</th>
                  <th className="hidden py-2 text-right font-semibold sm:table-cell">Retención</th>
                  <th className="hidden py-2 text-right font-semibold lg:table-cell">Abonado</th>
                  <th className="py-2 text-right font-semibold">Pendiente</th>
                  <th className="py-2 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {paginacionFacturas.visibles.map((factura) => (
                  <tr
                    key={factura.id}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2 font-mono text-xs whitespace-nowrap text-ink-800">
                      {factura.documento}
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {formatDateValue(factura.emitida)}
                    </td>
                    <td className="py-2 text-ink-600">{formatDateValue(factura.vencimiento)}</td>
                    <td className="hidden py-2 text-right tabular-nums text-ink-700 sm:table-cell">
                      {formatCurrency(factura.total)}
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-ink-600 sm:table-cell">
                      {formatCurrency(factura.retencion)}
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-emerald-600 lg:table-cell">
                      {formatCurrency(factura.abonado)}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums text-amber-600">
                      {formatCurrency(factura.pendiente)}
                    </td>
                    <td className="py-2">
                      <Badge variant={TONO_ESTADO[factura.estado] ?? 'neutral'}>
                        {factura.estado}
                        {factura.dias_vencidos > 0 ? ` · ${factura.dias_vencidos} d` : ''}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador
          {...paginacionFacturas}
          etiqueta="documentos"
          className="-mx-5 -mb-5 mt-3 rounded-b-card"
        />
      </SectionCard>

      <SectionCard
        title="Mis abonos"
        description={
          movimientos.data
            ? `${busquedaAbonos.trim() ? `${abonosFiltrados.length} de ${movimientosPeriodo.length}` : `${movimientosPeriodo.length}`} movimiento(s) en el período`
            : 'Cargando…'
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busquedaAbonos}
            onChange={setBusquedaAbonos}
            ariaLabel="Buscar abonos"
            placeholder="Buscar referencia, método o detalle…"
          />

          {movimientos.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : movimientosPeriodo.length === 0 ? (
            <EstadoVacio
              icon={Wallet}
              titulo="Sin abonos en esas fechas"
              detalle="Cuando registremos un pago tuyo aparecerá aquí."
            />
          ) : abonosFiltrados.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos abonos para “${busquedaAbonos.trim()}”.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusquedaAbonos('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Fecha</th>
                  <th className="py-2 font-semibold">Referencia</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Método</th>
                  <th className="hidden py-2 font-semibold xl:table-cell">Detalle</th>
                  <th className="py-2 text-right font-semibold">Monto</th>
                </tr>
              </thead>
              <tbody>
                {paginacionAbonos.visibles.map((movimiento, indice) => (
                  <tr
                    key={`${movimiento.referencia ?? 'pago'}-${indice}`}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2 whitespace-nowrap text-ink-600">
                      {formatDateValue(movimiento.fecha)}
                    </td>
                    <td className="py-2 font-mono text-xs text-ink-800">
                      {movimiento.referencia ?? '—'}
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">{movimiento.metodo}</td>
                    <td className="hidden py-2 text-ink-500 xl:table-cell">
                      {movimiento.detalle ?? '—'}
                    </td>
                    <td className="py-2 text-right font-semibold tabular-nums text-emerald-600">
                      {formatCurrency(movimiento.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador
          {...paginacionAbonos}
          etiqueta="movimientos"
          className="-mx-5 -mb-5 mt-3 rounded-b-card"
        />
      </SectionCard>
    </div>
  );
}
